// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — undo/redo history descriptors (D5)
// ═══════════════════════════════════════════════════════════════════════════
//
// Category: "system" — split out of `system.ts` for file-size hygiene
// (CLAUDE.md "Контроль размера файлов": system.ts is already ~1300 lines).
// Phase 4.1.k CLI undo/redo router (App.tsx). AutoCAD parity:
//
//   • U / UNDO         — undo the most recent action (bare form). CIS
//     alias ОТМ.
//   • UNDO BEGIN/END/BACK — open / commit / abort a named undo batch
//     group (nested groups supported; App.tsx tracks `historyBatchDepth`).
//     These are typed as ONE literal two-word alias each — AutoCAD's own
//     CLI accepts the sub-keyword as a SEPARATE prompt reply, but this
//     editor's single-line command box takes the whole phrase at once
//     (an established app-specific scripting convention, unchanged by
//     D5 — see App.tsx history router comment).
//   • REDO / MREDO     — redo the most recently undone action. AutoCAD's
//     MREDO additionally accepts a repeat count / Last / All keyword;
//     this editor treats REDO and MREDO as synonyms of the same single
//     redo-step action (a pre-existing simplification, not introduced by
//     D5 — out of scope for the router-extraction ticket).
//
// Reducer literals (history/undo, history/redo, history/begin-batch,
// history/commit-batch, history/abort-batch) are dispatched directly from
// App.tsx and consumed by the top-level history reducer in editorStore.ts.
// ═══════════════════════════════════════════════════════════════════════════

import type { CommandDescriptor } from "../types.js";

const REDUCER_HISTORY = "apps/web/src/app/state/editorStore.ts";

const UNDO: CommandDescriptor = {
  id: "UNDO",
  category: "system",
  status: "implemented",
  // History-navigation command — outside the AI drafting safe contour
  // (ТЗ §10.1: view/session-lifecycle commands are not AI-callable).
  aiCallable: false,
  aliases: { en: ["UNDO", "U"], ru: ["ОТМ"] },
  commandTypeLiterals: ["history/undo"],
  parameters: [],
  help: {
    ru: {
      short: "Отменяет последнее действие.",
      long: "Команда U / UNDO (ОТМ) отменяет последнее действие из стека истории. Отказывает с подсказкой, если стек пуст или если открыта именованная группа (UNDO BEGIN) — сначала нужно закрыть группу через UNDO END/BACK.",
      examples: ["U", "UNDO", "ОТМ"],
      seeAlso: ["REDO"],
    },
    en: {
      short: "Undoes the most recent action.",
      long: "The U / UNDO command undoes the most recent action from the history stack. It refuses with a hint when the stack is empty, or when a named batch group is open (UNDO BEGIN) — the group must be closed via UNDO END/BACK first.",
      examples: ["U", "UNDO"],
      seeAlso: ["REDO"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.undo.label",
    labelKeyEn: "command.undo.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "U",
  },
  reducerLocations: [
    { commandTypeLiteral: "history/undo", file: REDUCER_HISTORY },
  ],
};

const UNDO_BEGIN: CommandDescriptor = {
  id: "UNDO-BEGIN",
  category: "system",
  status: "implemented",
  aiCallable: false,
  aliases: { en: ["UNDO BEGIN"], ru: [] },
  commandTypeLiterals: ["history/begin-batch"],
  parameters: [],
  help: {
    ru: {
      short: "Открывает именованную группу отмены (batch).",
      long: "UNDO BEGIN открывает группу истории: последующие действия сворачиваются в один шаг отмены до UNDO END/BACK. Повторный UNDO BEGIN при уже открытой группе сначала коммитит текущую (AutoCAD-парность), затем открывает новую.",
      examples: ["UNDO BEGIN"],
      seeAlso: ["UNDO-END", "UNDO-BACK", "UNDO"],
    },
    en: {
      short: "Opens a named undo batch group.",
      long: "UNDO BEGIN opens a history group: subsequent actions collapse into a single undo step until UNDO END/BACK. A second UNDO BEGIN while a group is already open first commits the current one (AutoCAD parity), then opens a new one.",
      examples: ["UNDO BEGIN"],
      seeAlso: ["UNDO-END", "UNDO-BACK", "UNDO"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.undobegin.label",
    labelKeyEn: "command.undobegin.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    { commandTypeLiteral: "history/begin-batch", file: REDUCER_HISTORY },
  ],
};

const UNDO_END: CommandDescriptor = {
  id: "UNDO-END",
  category: "system",
  status: "implemented",
  aiCallable: false,
  aliases: { en: ["UNDO END"], ru: [] },
  commandTypeLiterals: ["history/commit-batch"],
  parameters: [],
  help: {
    ru: {
      short: "Завершает (коммитит) текущую группу отмены.",
      long: "UNDO END коммитит группу, открытую UNDO BEGIN, сворачивая накопленные действия в один шаг отмены. Для вложенных групп уменьшает глубину вложенности на единицу. Отказывает с подсказкой, если нет открытой группы.",
      examples: ["UNDO END"],
      seeAlso: ["UNDO-BEGIN", "UNDO-BACK"],
    },
    en: {
      short: "Commits the currently-open undo batch group.",
      long: "UNDO END commits the group opened by UNDO BEGIN, collapsing the accumulated actions into a single undo step. For nested groups it decrements the nesting depth by one. It refuses with a hint when no group is open.",
      examples: ["UNDO END"],
      seeAlso: ["UNDO-BEGIN", "UNDO-BACK"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.undoend.label",
    labelKeyEn: "command.undoend.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    { commandTypeLiteral: "history/commit-batch", file: REDUCER_HISTORY },
  ],
};

const UNDO_BACK: CommandDescriptor = {
  id: "UNDO-BACK",
  category: "system",
  status: "implemented",
  aiCallable: false,
  aliases: { en: ["UNDO BACK"], ru: [] },
  commandTypeLiterals: ["history/abort-batch"],
  parameters: [],
  help: {
    ru: {
      short: "Откатывает (отменяет целиком) текущую группу отмены.",
      long: "UNDO BACK отменяет группу, открытую UNDO BEGIN, целиком — все накопленные внутри неё действия откатываются, как если бы их не было. Отказывает с подсказкой, если нет открытой группы.",
      examples: ["UNDO BACK"],
      seeAlso: ["UNDO-BEGIN", "UNDO-END"],
    },
    en: {
      short: "Aborts (fully rolls back) the currently-open undo batch group.",
      long: "UNDO BACK aborts the group opened by UNDO BEGIN entirely — every action accumulated inside it is rolled back as if it never happened. It refuses with a hint when no group is open.",
      examples: ["UNDO BACK"],
      seeAlso: ["UNDO-BEGIN", "UNDO-END"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.undoback.label",
    labelKeyEn: "command.undoback.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    { commandTypeLiteral: "history/abort-batch", file: REDUCER_HISTORY },
  ],
};

const REDO: CommandDescriptor = {
  id: "REDO",
  category: "system",
  status: "implemented",
  aiCallable: false,
  aliases: { en: ["REDO", "MREDO"], ru: ["ПВТ"] },
  commandTypeLiterals: ["history/redo"],
  parameters: [],
  help: {
    ru: {
      short: "Повторяет последнее отменённое действие.",
      long: "Команда REDO (MREDO / ПВТ) повторяет последнее отменённое действие. Отказывает с подсказкой, если стек будущего пуст или если открыта именованная группа отмены. AutoCAD's MREDO дополнительно принимает число повторов / Last / All — в этом редакторе REDO и MREDO — синонимы одного одиночного шага повтора (существующее упрощение, не вводится этим PR).",
      examples: ["REDO", "MREDO", "ПВТ"],
      seeAlso: ["UNDO"],
    },
    en: {
      short: "Redoes the most recently undone action.",
      long: "The REDO (MREDO / ПВТ) command redoes the most recently undone action. It refuses with a hint when the future stack is empty, or when a named undo batch group is open. AutoCAD's MREDO additionally accepts a repeat count / Last / All keyword; in this editor REDO and MREDO are synonyms of the same single redo step (a pre-existing simplification, not introduced by this PR).",
      examples: ["REDO", "MREDO"],
      seeAlso: ["UNDO"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.redo.label",
    labelKeyEn: "command.redo.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    { commandTypeLiteral: "history/redo", file: REDUCER_HISTORY },
  ],
};

export const SYSTEM_HISTORY_DESCRIPTORS: readonly CommandDescriptor[] = [
  UNDO,
  UNDO_BEGIN,
  UNDO_END,
  UNDO_BACK,
  REDO,
];
